import { createSelector } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { blotterSlice } from '@/store/blotter/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { AlgoOrder } from '@/store/orders/algoOrders/state.ts';
import type { Order } from '@/store/orders/state.ts';
import type { AppState } from '@/store/store.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectAlgoTile = createSelector(
  (state: AppState, tileId: string) => state.orders[tileId] as Order | undefined,
  (order) => (isAlgoOrder(order) ? order : undefined),
);

export const isAlgoOrder = (order: unknown): order is AlgoOrder => {
  return (
    order !== null &&
    typeof order === 'object' &&
    ((order as AlgoOrder).type === 'Twap+Order' ||
      (order as AlgoOrder).type === 'NightjarOrder' ||
      (order as AlgoOrder).type === 'FalconOrder')
  );
};

export const selectOrderEntryByIdFromBlotter = createSelector(
  (state: AppState, orderId: string) => blotterSlice.selectors.getOrderEntryById(state, orderId),
  (order) => order,
);

export const selectAlgoErrors = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.errors;
  },
);

export const selectAlgoLimitPrice = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.limitPrice;
  },
);

export const selectAlgoExpiryDate = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.expiryDate;
  },
);

export const selectAlgoNDFFixingSource = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.ndfFixingSource;
  },
);

export const selectAlgoNDFFixingDate = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.ndfFixingDate;
  },
);

export const selectAlgoStartDate = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.startDate;
  },
);

export const selectTradeIdFromAlgoTile = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (order, tileId) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    if (
      order.properties.status.type === 'creating' ||
      order.properties.status.type === 'failed' ||
      order.properties.status.type === 'pending'
    ) {
      console.warn(`[AlgoOrderEditionTile] Tile with id ${tileId} is in creating state, no data available.`);
      return undefined;
    }

    return order.properties.status.tradeId;
  },
);

export const selectAlgoCurrencyPair = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return `${order.properties.currencyPair?.currency1}/${order.properties.currencyPair?.currency2}`;
  },
);

export const selectAlgoNotionalCurrencyIndex = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.notionalCurrencyIndex;
  },
);

export const selectAlgoLiquidityPool = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    return order.properties.liquidityPool;
  },
);

export const selectAlgoClippingMode = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type === 'FalconOrder' || order.type === 'NightjarOrder') {
      return undefined;
    }

    return order.properties.clippingMode;
  },
);

export const selectAlgoSpreadCapture = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type === 'FalconOrder' || order.type === 'NightjarOrder') {
      return undefined;
    }

    return order.properties.spreadCapture;
  },
);

export const selectAlgoClipSize = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type === 'FalconOrder' || order.type === 'NightjarOrder') {
      return undefined;
    }

    return order.properties.clipSize;
  },
);

export const selectAlgoAlphaSeeker = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (order) => {
    if (isNotDefined(order) || order.type === 'FalconOrder' || order.type === 'Twap+Order') {
      return undefined;
    }

    return order.properties.alphaSeeker;
  },
);

export const selectAlgoSpeed = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type === 'Twap+Order') {
      return undefined;
    }

    return order.properties.speed;
  },
);

export const selectAlgoNotificationEmails = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (state: AppState) => state.userPreferences,
  (order, userPreference) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return [];
    }

    if (isDefined(order.properties.notificationEmails)) {
      return order.properties.notificationEmails;
    }

    return userPreference.splitNotifications ? userPreference.splitNotificationsEmailsOrders : userPreference.emails;
  },
);

export const selectAlgoRandomize = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type === 'FalconOrder' || order.type === 'NightjarOrder') {
      return undefined;
    }

    return order.properties.randomize;
  },
);

export const selectAlgoNotionalData = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      notional: order.properties.notional,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectClientWayAlgoData = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (order) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      clientWay: order.properties.clientWay,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectSpotAlgoData = createSelector(
  (state: AppState, tileId: string) => selectAlgoTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (state: AppState, tileId: string) => {
    const order = selectAlgoTile(state, tileId);

    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder') ||
      isNotDefined(order.properties.currencyPair?.tiering) ||
      isNotDefined(order.properties.currencyPair?.currency1) ||
      isNotDefined(order.properties.currencyPair?.currency2)
    ) {
      return undefined;
    }

    const currencyPair = `${order.properties.currencyPair.currency1}/${order.properties.currencyPair.currency2}`;
    const espStreamId = getEspStreamId(currencyPair, order.properties.currencyPair.tiering);

    return state.esp.streams[espStreamId];
  },
  (order, tileId, espPrice) => {
    if (
      isNotDefined(order) ||
      (order.type !== 'Twap+Order' && order.type !== 'NightjarOrder' && order.type !== 'FalconOrder')
    ) {
      return undefined;
    }

    if (isNotDefined(order.properties.currencyPair?.tiering)) {
      console.warn(`[AlgoSpot] Tile with id ${tileId} has no tiering defined.`);
      return undefined;
    }

    return {
      spot:
        espPrice?.status === 'PRICED'
          ? convertPointsToRate(espPrice.midRate, espPrice.currencyPairPrecision)
          : undefined,
      digitCount: espPrice?.status === 'PRICED' ? espPrice.currencyPairPrecision - 1 : undefined,
    };
  },
);
